<template>
    <div :class="messageClass">
        <div class="conversation-message-container">
            <base-avatar
                v-if="!isMe"
                class="conversation-message-avatar"
                :name="this.getParticipantName(this.message.participant)"
                :photo="this.message.participant.photo"
                color="light"
                small
            />

            <div :class="contentClass">
                <conversation-message-content-text
                    v-if="type === 'text'"
                    :message-status="status"
                    :text="content.text"
                    :message-time="messageTime"
                />

                <conversation-message-content-image
                    v-else-if="type === 'image'"
                    :url="content.url"
                    :url_thumbnail="content.thumbnail"
                    :message-status="status"
                    :text="content.text"
                    :message-time="messageTime"
                />

                <conversation-message-content-audio
                    v-else-if="type === 'audio'"
                    :message="this.message"
                    :message-status="status"
                    @error="setError"
                    :message-time="messageTime"
                />

                <conversation-message-content-sticker
                    v-else-if="type === 'sticker'"
                    :message-status="status"
                    :message="this.message"
                    :message-time="messageTime"
                />

                <conversation-message-content-is-typing
                    v-else-if="type === 'is-typing'"
                />

                <conversation-message-content-chat-bot-ask-question
                    v-else-if="type === 'chat_bot_ask_question'"
                    :nickname="this.me().name"
                />
            </div>

            <div
                v-if="isDeletable"
                v-click-outside="hideDelete"
                class="relative"
            >
                <div
                    v-if="!this.isDeleteShown"
                    class="w-6 flex px-1 justify-center pb-1"
                    @click="setIsDeleteShown(true)"
                >
                    <font-awesome-icon
                        class="text-main"
                        :icon="['far', 'ellipsis-vertical']"
                    />
                </div>
                <div
                    v-if="this.isDeleteShown"
                    :class="deleteButtonClass"
                    @click="deleteMessage"
                >
                    <font-awesome-icon :icon="['fal', 'trash-can']" />
                    <span>{{ trans.get("chat.delete_this_message") }}</span>
                </div>
            </div>
        </div>

        <div v-if="hasError" class="conversation-message-error">
            <template v-if="isMessageNotSent(message)">
                {{ trans.get("chat.message_not_sent") }}
            </template>
            <template v-else-if="isMessageNotLoaded(message)">
                {{ trans.get("chat.audio_message_not_loaded") }}
            </template>
        </div>
    </div>
</template>

<style lang="scss">
.conversation-message {
    @apply w-full pt-1 px-3;

    .conversation-message-menu {
        @apply flex items-end w-6;
    }

    .delete-button {
        @apply absolute bottom-0 bg-white rounded-lg flex items-center gap-x-2 shadow-large
        p-3 text-sm text-neutral-800 hover:bg-neutral-100 cursor-pointer w-max;
    }

    .conversation-message-error {
        @apply mt-0.5 mb-2 text-danger font-semibold;
    }

    &--right {
        .conversation-message-container {
            @apply flex items-end flex-row-reverse;

            .conversation-message-content:not(
                    .conversation-message-content--sticker
                ) {
                @apply bg-primary text-white;
            }

            .conversation-message-content-text {
                a,
                a:visited {
                    @apply text-white hover:text-neutral-100 underline;
                }
            }

            .conversation-message-menu {
                @apply ml-10 md:ml-20;
            }

            .conversation-message-content-image--status {
                @apply bg-white text-dark;
            }

            .conversation-message-content-sticker-status {
                @apply bg-white text-dark;
            }
        }

        & + .conversation-message--left {
            @apply pb-5;
        }

        .conversation-message-error {
            @apply text-right;
        }
    }

    &--left {
        .conversation-message-container {
            @apply flex items-end flex-row;

            & + .conversation-message--left .conversation-message-avatar {
                @apply invisible;
            }

            .conversation-message-content:not(
                    .conversation-message-content--sticker
                ) {
                @apply bg-white text-dark;
            }

            .conversation-message-content-image--status {
                @apply bg-white text-dark;
            }

            .conversation-message-content-sticker-status {
                @apply bg-white text-dark;
            }

            .conversation-message-content-text {
                a,
                a:visited {
                    @apply text-primary hover:text-primary-hover underline;
                }
            }

            .conversation-message-menu {
                @apply mr-2 md:mr-6;
            }
        }

        & + .conversation-message--right {
            @apply pb-5;
        }

        .conversation-message-error {
            @apply ml-12;
        }
    }

    &--danger {
        .conversation-message-content {
            @apply bg-danger text-white;
        }
    }

    .conversation-message-avatar {
        @apply flex-none self-end mr-3 mb-0.5;
    }

    .conversation-message-content {
        @apply flex-shrink rounded-lg overflow-ellipsis overflow-hidden leading-normal;

        &--image {
            @apply max-w-xs;
        }
    }
}
</style>

<script>
import ConversationMessageContentText from "./ConversationMessageContentText";
import ConversationMessageContentImage from "./ConversationMessageContentImage";
import ConversationMessageContentAudio from "./ConversationMessageContentAudio";
import ConversationMessageContentIsTyping from "./ConversationMessageContentIsTyping";
import BaseAvatar from "../elements/BaseAvatar";
import ChatMixin from "./ChatMixin";
import ConversationMessageContentChatBotAskQuestion from "./ConversationMessageContentChatBotAskQuestion";
import BaseMenu from "../elements/BaseMenu";
import BaseButton from "../elements/BaseButton";
import BaseMenuItem from "../elements/BaseMenuItem";
import requests from "./requests";
import { mapActions, mapGetters } from "vuex";
import MessageMixin from "./MessageMixin";
import ConversationMessageContentSticker from "./ConversationMessageContentSticker.vue";
import moment from "moment-timezone";

export default {
    name: "ConversationMessage",
    mixins: [ChatMixin, MessageMixin],
    components: {
        ConversationMessageContentSticker,
        BaseMenuItem,
        BaseButton,
        BaseMenu,
        BaseAvatar,
        ConversationMessageContentAudio,
        ConversationMessageContentImage,
        ConversationMessageContentText,
        ConversationMessageContentIsTyping,
        ConversationMessageContentChatBotAskQuestion,
    },
    props: {
        message: {
            type: Object,
        },
    },
    computed: {
        ...mapGetters("chat", ["getSelectedConversation"]),
        type() {
            return this.message.type !== undefined && this.message.type;
        },
        content() {
            return this.message.content !== undefined && this.message.content;
        },
        status() {
            if (this.hasError) {
                return "error";
            }

            if (
                this.isMe &&
                this.message.read !== undefined &&
                this.message.read === false
            ) {
                return "sent";
            }

            if (
                this.isMe &&
                this.message.read !== undefined &&
                this.message.read === true
            ) {
                return "read";
            }

            return null;
        },
        isMe() {
            return (
                this.message.participant !== undefined &&
                this.message.participant.id === this.me().id
            );
        },
        hasError() {
            return this.hasMessageError(this.message);
        },
        canDeleteMessageForClient() {
            return (
                this.me().type === "client" &&
                (this.message.participant.type === "client" ||
                    this.message.participant.type === "offer")
            );
        },
        isDeletable() {
            return (
                this.message.participant.type === "master_care" ||
                this.canDeleteMessageForClient
            );
        },
        messageTime() {
            if (!this.isMe || this.status) {
                const dbTimezone = "Europe/Paris";
                const userTimeZone =
                    Intl.DateTimeFormat().resolvedOptions().timeZone;
                return moment
                    .tz(this.message.created_at, dbTimezone)
                    .tz(userTimeZone)
                    .format(this.config.get("chat.message_time_format"));
            }
            return null;
        },
        messageClass() {
            return {
                "conversation-message": true,
                "conversation-message--left":
                    this.message.participant !== undefined && !this.isMe,
                "conversation-message--right":
                    this.message.participant !== undefined && this.isMe,
                "conversation-message--danger": this.hasError,
            };
        },
        contentClass() {
            return {
                "conversation-message-content": true,
                "conversation-message-content--image": this.type === "image",
                "conversation-message-content--sticker":
                    this.type === "sticker",
            };
        },
        deleteButtonClass() {
            const isContentLong =
                this.message.content?.text &&
                this.message.content.text.length > 40;

            return {
                "delete-button": true,
                "left-0": !isContentLong && !this.isMe,
                "right-0": !isContentLong && this.isMe,
                "-left-52": isContentLong && !this.isMe,
                "-right-52": isContentLong && this.isMe,
            };
        },
    },
    methods: {
        ...mapActions("chat", ["removeMessageFromConversation"]),
        deleteMessage() {
            const chat_message_id = this.message.id;
            if (this.canDeleteMessageForClient) {
                requests.deleteMessageForClient(
                    { chat_message_id: chat_message_id },
                    () => {
                        const conversation = this.getSelectedConversation;
                        this.removeMessageFromConversation({
                            conversation,
                            chat_message_id,
                        });
                    },
                    () => {},
                );
            } else {
                requests.deleteMessage(
                    { chat_message_id: chat_message_id },
                    () => {
                        const conversation = this.getSelectedConversation;
                        this.removeMessageFromConversation({
                            conversation,
                            chat_message_id,
                        });
                    },
                    () => {},
                );
            }
        },
        setError(errorMessage) {
            this.error = errorMessage;
        },
        setIsDeleteShown(isDeleteShown) {
            this.isDeleteShown = isDeleteShown;
        },
        hideDelete() {
            this.isDeleteShown = false;
        },
    },
    mounted() {
        this.scrollMessagesToBottom();
    },
    data() {
        return {
            error: null,
            isDeleteShown: false,
        };
    },
};
</script>
